import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createPet, deletePet, listPets, updatePet } from '../../services/portal'
import type { PortalPet } from '../../types'

const SPECIES = ['dog', 'cat', 'rabbit', 'bird', 'reptile', 'other'] as const

const SPECIES_EMOJI: Record<string, string> = {
  dog: '🐕', cat: '🐈', rabbit: '🐇', bird: '🐦', reptile: '🦎', other: '🐾',
}

const EMPTY: Partial<PortalPet> = { name: '', species: 'dog', breed: '', sex: '', notes: '' }

export default function PortalPetsPage() {
  const qc = useQueryClient()
  const { data: pets = [], isLoading, isError } = useQuery({ queryKey: ['portal', 'pets'], queryFn: listPets })
  const [editing, setEditing] = useState<Partial<PortalPet> | null>(null)

  const save = useMutation({
    mutationFn: (p: Partial<PortalPet>) => p.id ? updatePet(p.id, p) : createPet(p),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal', 'pets'] })
      qc.invalidateQueries({ queryKey: ['portal', 'dashboard'] })
      setEditing(null)
    },
  })

  const remove = useMutation({
    mutationFn: (id: number) => deletePet(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal', 'pets'] })
      qc.invalidateQueries({ queryKey: ['portal', 'dashboard'] })
    },
  })

  return (
    <div className="space-y-7">
      {/* Header */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-fraunces text-4xl font-[400] tracking-tight text-nala-ink">
            Your pets
          </h1>
          <p className="text-nala-mute mt-1.5 text-[15px]">Manage the pets your vet sees on each visit.</p>
        </div>
        <button
          onClick={() => setEditing(EMPTY)}
          className="shrink-0 inline-flex items-center gap-2 bg-nala-accent hover:bg-nala-accent/90 text-nala-accentInk font-semibold rounded-full px-5 py-2.5 text-sm transition"
        >
          + Add pet
        </button>
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-sm text-nala-mute">Loading…</div>
      ) : isError ? (
        <div className="py-12 text-center text-sm text-nala-mute">
          Unable to load. Please refresh or sign in again.
        </div>
      ) : pets.length === 0 ? (
        <div className="bg-white border border-nala-line rounded-3xl p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-nala-soft flex items-center justify-center text-3xl mx-auto mb-4">🐾</div>
          <h3 className="font-fraunces text-2xl font-[400] text-nala-ink">No pets yet</h3>
          <p className="text-center text-sm text-nala-mute py-2">No pets found. Add your first furry family member to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {pets.map((pet) => (
            <div
              key={pet.id}
              className="bg-white border border-nala-line rounded-2xl p-5 flex gap-4 hover:shadow-sm transition-shadow"
            >
              <div className="w-14 h-14 rounded-2xl bg-nala-soft flex items-center justify-center text-3xl shrink-0">
                {SPECIES_EMOJI[pet.species ?? 'other'] ?? '🐾'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-fraunces text-xl font-[500] text-nala-ink leading-snug">{pet.name}</h3>
                  <div className="flex gap-3 shrink-0">
                    <button
                      onClick={() => setEditing(pet)}
                      className="text-sm text-nala-clay font-semibold hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Remove ${pet.name}?`)) remove.mutate(pet.id)
                      }}
                      className="text-sm text-nala-mute hover:text-red-600 transition"
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <p className="text-sm text-nala-mute capitalize mt-0.5">
                  {[pet.species, pet.breed].filter(Boolean).join(' · ')}
                </p>
                {pet.notes && (
                  <p className="text-sm text-nala-mute mt-2 leading-relaxed">{pet.notes}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <PetModal
          pet={editing}
          onClose={() => setEditing(null)}
          onSave={(p) => save.mutate(p)}
          saving={save.isPending}
          error={save.isError ? 'Could not save. Please check the fields and try again.' : null}
        />
      )}
    </div>
  )
}

function PetModal({
  pet, onClose, onSave, saving, error,
}: {
  pet: Partial<PortalPet>
  onClose: () => void
  onSave: (p: Partial<PortalPet>) => void
  saving: boolean
  error: string | null
}) {
  const [draft, setDraft] = useState(pet)

  return (
    <div
      className="fixed inset-0 bg-nala-ink/40 z-40 flex items-center justify-center p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-fraunces text-2xl font-[400] text-nala-ink mb-5">
          {pet.id ? 'Edit pet' : 'Add a new pet'}
        </h2>
        <form
          className="space-y-4"
          onSubmit={(e) => { e.preventDefault(); onSave(draft) }}
        >
          <NalaField label="Name" required>
            <NalaInput
              required
              value={draft.name || ''}
              onChange={(v) => setDraft({ ...draft, name: v })}
              placeholder="e.g. Waffles"
            />
          </NalaField>

          <NalaField label="Species">
            <select
              value={draft.species || 'dog'}
              onChange={(e) => setDraft({ ...draft, species: e.target.value })}
              className="w-full rounded-xl border border-nala-line px-3.5 py-2.5 text-sm text-nala-ink capitalize focus:border-nala-accent focus:outline-none focus:ring-1 focus:ring-nala-accent"
            >
              {SPECIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </NalaField>

          <div className="grid grid-cols-2 gap-3">
            <NalaField label="Breed">
              <NalaInput
                value={draft.breed || ''}
                onChange={(v) => setDraft({ ...draft, breed: v })}
                placeholder="e.g. Labrador"
              />
            </NalaField>
            <NalaField label="Birth date">
              <input
                type="date"
                value={draft.birth_date || ''}
                onChange={(e) => setDraft({ ...draft, birth_date: e.target.value })}
                className="w-full rounded-xl border border-nala-line px-3.5 py-2.5 text-sm text-nala-ink focus:border-nala-accent focus:outline-none focus:ring-1 focus:ring-nala-accent"
              />
            </NalaField>
          </div>

          <NalaField label="Notes">
            <textarea
              value={draft.notes || ''}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              rows={3}
              placeholder="Allergies, behavior, anything your vet should know"
              className="w-full rounded-xl border border-nala-line px-3.5 py-2.5 text-sm text-nala-ink focus:border-nala-accent focus:outline-none focus:ring-1 focus:ring-nala-accent resize-none"
            />
          </NalaField>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-sm text-nala-mute hover:text-nala-ink transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-nala-accent hover:bg-nala-accent/90 text-nala-accentInk font-semibold px-6 py-2.5 rounded-full text-sm disabled:opacity-60 transition"
            >
              {saving ? 'Saving…' : 'Save pet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function NalaField({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-nala-mute uppercase tracking-wide mb-1.5">
        {label} {required && <span className="text-nala-clay">*</span>}
      </span>
      {children}
    </label>
  )
}

function NalaInput({
  value, onChange, placeholder, required,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  required?: boolean
}) {
  return (
    <input
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-xl border border-nala-line px-3.5 py-2.5 text-sm text-nala-ink focus:border-nala-accent focus:outline-none focus:ring-1 focus:ring-nala-accent placeholder:text-nala-line"
    />
  )
}
